import { createContext } from 'react';
import type { SendEvaluatedBy, SendResultReport, SendReviewState } from '@/model/Agent.model';
import { capabilityOf } from './SendStoredReport';

/** What the report knows about one rule: how the run checked it, and whether our reading of it awaits review. */
export interface SendRuleProvenance {
  /** Absent for a rule nothing reached, and for one of our own checks, which has no outcome row. */
  evaluatedBy?: SendEvaluatedBy;
  /** Absent from a result stored before review states were recorded. */
  reviewState?: SendReviewState;
}

/** An outcome row as stored: one stored before these were recorded has neither field. */
interface StoredOutcome {
  rule_id: string;
  evaluated_by?: SendEvaluatedBy;
  review_state?: SendReviewState;
}

/**
 * What the report knows about each rule, keyed by the id its findings cite. A finding from a rule we
 * encoded from CDISC's text cites the register rule exactly as one from CDISC's own encoding does, so the
 * only way to tell them apart is to join the finding's rule to the run's outcome rows; only rows that ran
 * carry `evaluated_by`. Every row, and every one of our own checks, carries whether our reading of it
 * awaits a practitioner's review.
 */
export const ruleProvenanceOf = (report: SendResultReport): ReadonlyMap<string, SendRuleProvenance> => {
  const outcomes: Array<StoredOutcome> = [...report.rule_outcomes, ...report.encoding_outcomes];
  const byRule = outcomes.reduce<Map<string, SendRuleProvenance>>((rules, outcome) => {
    if (outcome.evaluated_by !== undefined || outcome.review_state !== undefined) {
      rules.set(outcome.rule_id, { evaluatedBy: outcome.evaluated_by, reviewState: outcome.review_state });
    }
    return rules;
  }, new Map());
  capabilityOf(report)?.in_house_checks.entries.forEach((check) => {
    byRule.set(check.code, { reviewState: check.review_state });
  });
  return byRule;
};

/** The run's rule provenance, for every rule id the report shows. Empty outside a report. */
export const SendRuleProvenanceContext = createContext<ReadonlyMap<string, SendRuleProvenance>>(new Map());

/** Said beside a rule whose reading is still ours. Practitioner questions never stop a check running. */
export const REVIEW_PENDING_TEXT = {
  label: 'Our reading, awaiting expert review',
  title:
    "Our tier and reading of this rule are awaiting a SEND practitioner's review. The check runs on our reading in the meantime.",
};

/**
 * How each way a rule can be checked reads beside its id, short and in full. The short label sits next to
 * the publisher chip, which already names CDISC, so it says who checked the rule and never repeats who
 * published it.
 */
export const PROVENANCE_TEXT: Record<SendEvaluatedBy, { label: string; title: string }> = {
  published_encoding: { label: 'Published check', title: "Checked by CDISC's published encoding of the rule" },
  in_house_check: {
    label: 'Our audited check',
    title: "Checked by this validator's own check, audited against CDISC's encoding",
  },
  derived_check: {
    label: 'Our reading of the rule',
    title: "Checked by an encoding we wrote from CDISC's published text, not by CDISC's own check",
  },
};
